import dayjs from 'dayjs'
import { useMemo } from 'react'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/table'

import type {
  ResGetCaseVisaBulletinTrendItem,
  ResGetCaseVisaBulletinTrendQuery,
} from '@/lib/queries/case/public/trend/visa-bulletin/types'
import { cn } from '@/lib/utils'
import { renderVisaBulletinDate } from '@/lib/utils/visa-bulletin'

interface Props {
  data: ResGetCaseVisaBulletinTrendQuery
  className?: string
}

export const VisaBulletinTrendTable: React.FC<Props> = ({ data, className }) => {
  const grouped = useMemo(() => {
    return data.reduce<ResGetCaseVisaBulletinTrendItem[][]>((accum, x) => {
      if (accum.length === 0) {
        return [[x]]
      }

      const lastGroup = accum[accum.length - 1]
      const lastYear = dayjs(lastGroup[0].bulletinDate).year()

      if (lastYear === dayjs(x.bulletinDate).year()) {
        return [...accum.slice(0, -1), [...lastGroup, x]]
      }

      return [...accum, [x]]
    }, [])
  }, [data])

  return (
    <div className={cn('relative max-h-[344px] overflow-y-auto rounded-md border', className)}>
      {grouped.map((group) => (
        <YearlyTable key={group[0].bulletinDate} data={group} />
      ))}
    </div>
  )
}

const YearlyTable: React.FC<Props> = ({ data }) => {
  return (
    <div className="border-b last:border-b-0 [&>div]:overflow-visible">
      <Table>
        <TableHeader className="z-1 sticky top-0 bg-secondary text-sm font-medium after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-border">
          <TableRow>
            <TableHead className="py-[10.5px]">{dayjs(data[0].bulletinDate).format('YYYY')}</TableHead>
            <TableHead className="py-[10.5px] text-xs">Final Action Date</TableHead>
            <TableHead className="py-[10.5px] text-xs">Date for Filing</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((x) => (
            <TableRow key={x.bulletinDate} className="border-b">
              <TableCell className="py-[10.5px]">{dayjs(x.bulletinDate).format('MMMM')}</TableCell>
              <TableCell className="py-[10.5px]">
                {renderVisaBulletinDate(x.finalActionDate, x.finalActionDateType)}
              </TableCell>
              <TableCell className="py-[10.5px]">{renderVisaBulletinDate(x.filingDate, x.filingDateType)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
